import { Logger, TLogger } from '@streaming/mouflon-utils';

import { EABRRule, IAbrControllerConfig } from '@abrController/AbrController.config';
import { DeltaBufferEstimator } from '@abrController/Estimators/DeltaBufferEstimator/DeltaBufferEstimator';
import { DroppedFramesEstimator } from '@abrController/Estimators/DroppedFramesEstimator/DroppedFramesEstimator';
import { IEstimator, IEstimatorConstructor } from '@abrController/Estimators/IEstimator';
import { InsufficientBufferEstimator } from '@abrController/Estimators/InsufficientBufferEstimator/InsufficientBufferEstimator';
import { MaximalResolutionsEstimator } from '@abrController/Estimators/MaxResolutionsEstimator/MaximalResolutionsEstimator';
import { MinimalBufferToIncreaseEstimator } from '@abrController/Estimators/MinimalBufferToIncreaseEstimator/MinimalBufferToIncreaseEstimator';
import { SwitchEstimator } from '@abrController/Estimators/SwitchEstimator/SwitchEstimator';
import { ThroughputEstimator } from '@abrController/Estimators/ThroughputEstimator/ThroughputEstimator';
import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { IBufferMetrics } from '@bufferController/BufferMetrics/IBufferMetrics';
import { ConfigConsumer } from '@configProvider/ConfigConsumer';
import { EDebuggableModuleState, IDebbugable, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { IResolution } from '@resolutionsManager/IResolutionsManager';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import bind from '@utils/decorators/bind';

const RuleToEstimatorMap: Record<EABRRule, IEstimatorConstructor> = {
  [EABRRule.DROPPED_FRAMES_ESTIMATOR]: DroppedFramesEstimator,
  [EABRRule.THROUGHPUT_ESTIMATOR]: ThroughputEstimator,
  [EABRRule.SWITCH_HISTORY]: SwitchEstimator,
  [EABRRule.INSUFFICIENT_BUFFER]: InsufficientBufferEstimator,
  [EABRRule.MINIMAL_BUFFER_TO_INCREASE]: MinimalBufferToIncreaseEstimator,
  [EABRRule.DELTA_BUFFER]: DeltaBufferEstimator,
  [EABRRule.MAX_RESOLUTION_ESTIMATOR]: MaximalResolutionsEstimator,
};

export class AbrController extends ConfigConsumer<IAbrControllerConfig> implements IDebbugable {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public groups: string[] = ['general', 'abr'];
  public additionalInfo: TDebuggableAdditionalInfo = { switchReasonHistory: [] };
  private _currentResolution: IResolution | null = null;
  private _availableResolutions: IResolution[] = [];
  private _estimators: Record<string, IEstimator> = {};
  private _lastSwitchTime: number = 0;
  private readonly _onResolutionChangedListener: string;
  private readonly _onResolutionsAvailableListener: string;
  private readonly _onMetricsReadyListener: string;

  private _logger: TLogger;

  constructor(eventBus: TEventBus) {
    super('abr', eventBus);
    this._logger = Logger(eventBus, 'Doppio - AbrController');

    this._logger = Logger(this._eventBus, 'Doppio - AbrController');

    this._onResolutionChangedListener = this._eventBus.subscribe(ResolutionsManagerEvents.resolutionChanged, this._onResolutionChanged);
    this._onResolutionsAvailableListener = this._eventBus.subscribe(
      ResolutionsManagerEvents.resolutionsAvailable,
      this._onResolutionsAvailable,
    );
    this._onMetricsReadyListener = this._eventBus.subscribe(BufferControllerEvents.bufferMetricsAvailable, this._recalculateCurrentLevel);

    this._recreateEstimators();
    this._lastSwitchTime = Date.now();
  }

  destroy(): void {
    super.destroy();

    this._eventBus.unsubscribe(this._onMetricsReadyListener);
    this._eventBus.unsubscribe(this._onResolutionChangedListener);
    this._eventBus.unsubscribe(this._onResolutionsAvailableListener);

    Object.values(this._estimators).forEach((estimator) => {
      estimator.destroy();
    });

    this._estimators = {};
  }

  @bind
  public _handleConfigUpdated(updatedValues: Partial<IAbrControllerConfig>): void {
    if (updatedValues.enabledABRRules) {
      this._recreateEstimators();
    }
  }

  private _recreateEstimators(): void {
    Object.values(this._estimators).forEach((estimator) => estimator.destroy());
    this._estimators = this.config.enabledABRRules.reduce<Record<string, IEstimator>>((estimators, rule) => {
      estimators[rule] = new RuleToEstimatorMap[rule](this._eventBus);
      return estimators;
    }, {});
  }

  @bind
  private _recalculateCurrentLevel(metrics: IBufferMetrics): void {
    const estimationsByName: Record<string, IResolution | null> = Object.entries(this._estimators).reduce<
      Record<string, IResolution | null>
    >((s, [estimatorName, estimator]) => {
      s[estimatorName] = estimator.getNextEstimatedResolution(this._availableResolutions, this._currentResolution, metrics);
      return s;
    }, {});

    this.additionalInfo.estimations = Object.entries(estimationsByName).reduce<Record<string, string>>((s, [name, estimator]) => {
      s[name] = estimator?.name || 'null';
      return s;
    }, {});

    if (Date.now() - this._lastSwitchTime < this.config.cooldownPeriod) {
      return;
    }

    if (!Object.values(this._estimators).length) {
      return;
    }

    const estimations: Array<IResolution | null> = Object.values(estimationsByName);

    const minimalRequestedResolution: IResolution | undefined = estimations
      .filter((resolution) => resolution)
      .sort((a, b) => (a?.bitrate || 0) - (b?.bitrate || 0))[0] as IResolution | undefined;

    const isChangingResolution: boolean = !!minimalRequestedResolution && minimalRequestedResolution.name !== this._currentResolution?.name;

    if (isChangingResolution) {
      const matchingRules: string[] = Object.entries(estimationsByName).reduce<string[]>((rules, [estimatorName, estimator]) => {
        if (estimator?.name === minimalRequestedResolution?.name) {
          rules.push(estimatorName);
        }

        return rules;
      }, []);

      this.additionalInfo.switchReasonHistory.unshift(
        `${this._currentResolution?.name} -> ${minimalRequestedResolution?.name}; ${matchingRules.join(',')}`,
      );
      this._lastSwitchTime = Date.now();
      this.additionalInfo.lastSwitchTime = this._lastSwitchTime;
      this.additionalInfo.lastSwitchReason = Object.entries(estimationsByName)
        .filter(([, resolution]) => resolution?.name === minimalRequestedResolution?.name)
        .map(([name]) => name)
        .join('; ');

      this._logger.info(`ABR wants to change the resolution, ${this._currentResolution?.name} -> ${minimalRequestedResolution?.name}`);
      this._eventBus.publish(ResolutionsManagerEvents.requestResolutionChange, minimalRequestedResolution);
    }
  }

  @bind
  private _onResolutionsAvailable(resolutions: IResolution[]): void {
    this._availableResolutions = resolutions;
  }

  @bind
  private _onResolutionChanged(resolution: IResolution): void {
    this._currentResolution = resolution;
    Object.values(this._estimators).forEach((estimator) => estimator.reset());
  }

  get additionalModules(): Record<string, IDebbugable> {
    return this._estimators;
  }
}
